import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { LanguageToken } from '@sleed/bizcommerce-api';
import { languagesApi } from '~/lib/api/client';
import { formToToken, type LanguageFormValues } from '~/lib/schemas/language';

export const languageKeys = {
	all: ['languages'] as const,
	list: () => [...languageKeys.all, 'list'] as const
};

/**
 * Languages list. Mirrors the legacy admin grid's `/languages/getall` (loadMode 'raw'):
 * the endpoint returns the full set in a `LanguageTokenList.items` envelope — no paging.
 */
export function useLanguagesList() {
	return useQuery<LanguageToken[]>({
		queryKey: languageKeys.list(),
		queryFn: async () => (await languagesApi.getAll()).data.items ?? []
	});
}

/** Create / update / delete a language (parity with the legacy CustomStore insert/update/remove). */
export function useLanguageMutations() {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: languageKeys.all });

	const create = useMutation({
		mutationFn: async (form: LanguageFormValues) => (await languagesApi.insert(formToToken(form))).data,
		onSuccess: invalidate
	});

	const update = useMutation({
		mutationFn: async (form: LanguageFormValues) => (await languagesApi.update(formToToken(form))).data,
		onSuccess: invalidate
	});

	const remove = useMutation({
		mutationFn: async (id: number) => (await languagesApi.remove(id)).data,
		onSuccess: invalidate
	});

	return { create, update, remove };
}
